import apiClient from './apiClient';
import config from '../utils/config';
import { extractDataFromResponse } from '../utils/dataUtils';

/**
 * Service for project-related API calls
 */
const projectService = {
  /**
   * Create a new project
   * @param {Object} data - Project data
   * @returns {Promise} Promise resolving to the created project
   */
  createProject: async (data) => {
    try {
      const response = await apiClient.post(config.PROJECT_ENDPOINTS.ALL, data);
      console.log('Create project response:', response);
      
      // Handle different potential response structures
      if (response?.data?.project) {
        return response.data.project;
      } else if (response?.project) {
        return response.project;
      } else if (response?.data) {
        return response.data;
      }
      
      return response;
    } catch (error) {
      console.error('Error creating project:', error);
      throw error;
    }
  },

  /**
   * Get all projects
   * @returns {Promise} Promise resolving to the projects data
   */
  getAllProjects: async () => {
    try {
      const response = await apiClient.get(config.PROJECT_ENDPOINTS.ALL);
      console.log('All projects response:', response);
      
      // Handle different potential response structures
      if (response?.data?.projects) {
        return response.data.projects;
      } else if (Array.isArray(response?.data)) {
        return response.data;
      } else if (Array.isArray(response)) {
        return response;
      }
      
      console.warn('Projects data structure not as expected:', response);
      return [];
    } catch (error) {
      console.error('Error fetching all projects:', error);
      throw error;
    }
  },

  /**
   * Get SDK Config for a project
   * @param {string} projectId - Project ID
   * @returns {Promise} Promise resolving to the SDK config data
   */
  getSDKConfig: async (projectId) => {
    try {
      const response = await apiClient.get(config.PROJECT_ENDPOINTS.SDK.CONFIG(projectId));
      console.log('SDK Config response:', response);
      
      // The backend returns data in { status, data: { sdkConfig } } format
      if (response?.data?.sdkConfig) {
        return response.data.sdkConfig;
      } else if (response?.sdkConfig) {
        return response.sdkConfig;
      } else if (response?.data) {
        return response.data;
      } else if (response?.config) {
        return response.config;
      }
      
      console.warn('SDK Config data structure not as expected:', response);
      // Return empty object with default values to prevent errors
      return {
        customCss: '',
        initialMessage: 'Hi there! How can I help you today?',
        chatbotName: 'Assistant',
        logo: '',
        backgroundColor: '#FFFFFF',
        borderColor: '#E5E7EB',
        textColor: '#1F2937',
        fontFamily: 'Inter, sans-serif',
        fontSize: '14px',
        primaryColor: '#3B82F6',
        secondaryColor: '#F3F4F6',
        buttonColor: '#3B82F6',
        buttonTextColor: '#FFFFFF'
      };
    } catch (error) {
      console.error('Error getting SDK config:', error);
      throw error;
    }
  },

  /**
   * Update SDK Config for a project
   * @param {string} projectId - Project ID
   * @param {Object} data - SDK configuration data
   * @returns {Promise} Promise resolving to the updated SDK config
   */
  updateSDKConfig: async (projectId, data) => {
    try {
      const response = await apiClient.patch(config.PROJECT_ENDPOINTS.SDK.CONFIG(projectId), data);
      return extractDataFromResponse(response);
    } catch (error) {
      console.error('Error updating SDK config:', error);
      throw error;
    }
  },

  /**
   * Get SDK Script URL for a project
   * @param {string} projectId - Project ID
   * @param {boolean} force - Whether to force regeneration of the script
   * @returns {Promise} Promise resolving to the SDK script URL
   */
  getSDKScript: async (projectId, force = false) => {
    try {
      const url = `${config.PROJECT_ENDPOINTS.SDK.SCRIPT(projectId)}${force ? '?force=true' : ''}`;
      const response = await apiClient.get(url);
      return extractDataFromResponse(response);
    } catch (error) {
      console.error('Error getting SDK script:', error);
      throw error;
    }
  },

  /**
   * Get project details
   * @param {string} projectId - Project ID
   * @returns {Promise} Promise resolving to the project details
   */
  getProject: async (projectId) => {
    try {
      const response = await apiClient.get(
        config.PROJECT_ENDPOINTS.DETAILS(projectId)
      );
      console.log('Project details response:', response);
      
      // Handle different potential response structures
      if (response?.data?.project) {
        return { data: { project: response.data.project } };
      } else if (response?.project) {
        return { data: { project: response.project } };
      } else if (response?.data) {
        return { data: { project: response.data } };
      }
      
      console.warn('Project data structure not as expected:', response);
      return { data: { project: {} } };
    } catch (error) {
      console.error('Error fetching project details:', error);
      throw error;
    }
  },

  /**
   * Update project details
   * @param {string} projectId - Project ID
   * @param {Object} data - Updated project data
   * @returns {Promise} Promise resolving to the updated project
   */
  updateProject: async (projectId, data) => {
    try {
      const response = await apiClient.patch(
        config.PROJECT_ENDPOINTS.DETAILS(projectId),
        data
      );
      console.log('Update project response:', response);
      
      // Handle different potential response structures
      if (response?.data?.project) {
        return response.data.project;
      } else if (response?.project) {
        return response.project;
      } else if (response?.data) {
        return response.data;
      }
      
      return response;
    } catch (error) {
      console.error('Error updating project:', error);
      throw error;
    }
  },

  /**
   * Delete a project
   * @param {string} id - Project ID
   * @returns {Promise} Promise resolving to the delete response
   */
  deleteProject: async (id) => {
    try {
      const response = await apiClient.delete(config.PROJECT_ENDPOINTS.DETAILS(id));
      return response;
    } catch (error) {
      console.error('Error deleting project:', error);
      throw error;
    }
  },

  /**
   * Duplicate a project
   * @param {string} projectId - Project ID to duplicate
   * @param {Object} data - New project data (name, clientName, description)
   * @returns {Promise} Promise resolving to the duplicated project
   */
  duplicateProject: async (projectId, data) => {
    try {
      const response = await apiClient.post(`${config.PROJECT_ENDPOINTS.DETAILS(projectId)}/duplicate`, data);
      console.log('Duplicate project response:', response);
      
      // Handle different potential response structures
      if (response?.data?.project) {
        return response.data.project;
      } else if (response?.project) {
        return response.project;
      } else if (response?.data) {
        return response.data;
      }
      
      return response;
    } catch (error) {
      console.error('Error duplicating project:', error);
      throw error;
    }
  },

  /**
   * Get project API key
   * @param {string} projectId - Project ID
   * @returns {Promise} Promise resolving to the API key
   */
  getApiKey: async (projectId) => {
    try {
      const response = await apiClient.get(
        config.PROJECT_ENDPOINTS.API_KEY(projectId)
      );
      console.log('Get API key response:', response);
      
      // Handle different potential response structures
      if (response?.data?.apiKey) {
        return response.data.apiKey;
      } else if (response?.apiKey) {
        return response.apiKey;
      }
      
      return response;
    } catch (error) {
      console.error('Error fetching API key:', error);
      throw error;
    }
  },

  /**
   * Regenerate project API key
   * @param {string} projectId - Project ID
   * @returns {Promise} Promise resolving to the new API key
   */
  regenerateApiKey: async (projectId) => {
    try {
      const response = await apiClient.post(
        config.PROJECT_ENDPOINTS.REGENERATE_API_KEY(projectId)
      );
      console.log('Regenerate API key response:', response);
      
      // Handle different potential response structures
      if (response?.data?.apiKey) {
        return response.data.apiKey;
      } else if (response?.apiKey) {
        return response.apiKey;
      }
      
      return response;
    } catch (error) {
      console.error('Error regenerating API key:', error);
      throw error;
    }
  },

  // WhatsApp Configuration
  async saveWhatsAppConfig(projectId, config) {
    const response = await apiClient.post(`/whatsapp/config/${projectId}`, config);
    return response.data;
  },

  async getWhatsAppConfig(projectId) {
    try {
      console.log('Fetching WhatsApp config for projectId:', projectId);
      const response = await apiClient.get(`/whatsapp/config/${projectId}`);
      console.log('WhatsApp config response received:', response);
      
      // Handle response data structure correctly
      if (response && response.data) {
        // Check if data is in response.data.data structure (common in our API)
        if (response.data.data) {
          console.log('WhatsApp config found in response.data.data:', response.data.data);
          return response.data.data;
        }
        // If just a data field with direct values
        console.log('WhatsApp config found in response.data:', response.data);
        return response.data;
      }
      
      console.warn('Unexpected WhatsApp config response format:', response);
      return null;
    } catch (error) {
      console.error('Error fetching WhatsApp config:', error);
      throw error;
    }
  },

  /**
   * Generate a prompt using AI
   * @param {string} projectId - The project ID
   * @param {object} promptData - The prompt generation data
   * @param {string} promptData.promptType - 'primary' or 'secondary'
   * @param {string} promptData.promptDescription - User's description
   * @param {string} promptData.mode - 'new' or 'modify'
   * @param {string} promptData.currentPrompt - Current prompt text if modifying
   * @returns {Promise<object>} - The generated prompt data
   */
  generatePrompt: async (projectId, promptData) => {
    try {
      const response = await apiClient.post(`/projects/${projectId}/prompts/generate`, promptData);
      return response.data;
    } catch (error) {
      console.error('Error generating prompt:', error);
      throw error;
    }
  },
};

export default projectService; 